// ═══════════════════════════════════════════════════════════════════════════
// IEPS, IVA y totales
//
// Extraído literal de index.html v0.9.16 (getIepsRateForGL línea 301,
// calcIepsBreakdown línea 308, totales líneas 2591-2598).
//
// ⚠ La aritmética de punto flotante se conserva EXACTAMENTE, en el mismo
//   orden de operaciones. Cambiarla rompería la equivalencia con los 43
//   documentos ya emitidos. La exactitud decimal llega en la Etapa 4, cuando
//   el cálculo pase al servidor con numeric(14,4).
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Tasa de IEPS según grados de alcohol.
 * LIEPS Art. 2, Fracción I, inciso A).
 */
export function getIepsRateForGL(gl) {
  if (gl <= 14) return 26.5;   // cerveza y vinos hasta 14° G.L.
  if (gl <= 20) return 30;     // fortificados, vermú (>14° y ≤20°)
  return 53;                   // destilados y licores (>20°)
}

/**
 * Desglose de IEPS por tasa, sobre líneas ya descontadas.
 * @param activeLines [{ productId, qty }]
 * @param products    catálogo
 * @param getPrice    (productId) => precio unitario
 * @param discount    porcentaje de descuento del documento
 */
export function calcIepsBreakdown(activeLines, products, getPrice, discount) {
  const byRate = {};
  let totalIeps = 0;

  activeLines.forEach(li => {
    const prod = products.find(p => p.id === li.productId);
    const rate = prod?.iepsRate ?? getIepsRateForGL(prod?.gl ?? 13);
    // Lo COBRABLE, no lo entregado: las botellas de cortesía salen de la
    // bodega y no causan IEPS porque no se facturan (migración 0060). Tiene
    // que coincidir con lo que calcula `crear_remision`, o el servidor avisa
    // de que los totales difieren sobre una remisión que está bien.
    const lineTotal = getPrice(li.productId) * (li.qty - (li.cortesia || 0));
    const lineAfterDiscount = lineTotal * (1 - discount / 100);
    const lineIeps = lineAfterDiscount * (rate / 100);

    totalIeps += lineIeps;
    if (!byRate[rate]) byRate[rate] = { base: 0, ieps: 0, rate };
    byRate[rate].base += lineAfterDiscount;
    byRate[rate].ieps += lineIeps;
  });

  return {
    totalIeps,
    byRate: Object.values(byRate).sort((a, b) => a.rate - b.rate),
  };
}

/**
 * Totales completos de un documento.
 * Réplica exacta de las líneas 2591-2598 del monolito.
 *
 * Nota fiscal: el IVA se calcula sobre (subtotal + IEPS) — el IEPS forma parte
 * de la base gravable del IVA. Esto NO cambia según el cliente pida el IEPS
 * desglosado o incluido; sólo cambia cómo se presenta.
 */
export function calcularTotales({ activeLines, products, getPrice, discount = 0, ivaRate = 16 }) {
  const subtotalBeforeDiscount = activeLines.reduce(
    (sum, li) => sum + getPrice(li.productId) * li.qty, 0);
  const discountAmount = subtotalBeforeDiscount * (discount / 100);
  const subtotal       = subtotalBeforeDiscount - discountAmount;
  const iepsBreakdown  = calcIepsBreakdown(activeLines, products, getPrice, discount);
  const iepsAmount     = iepsBreakdown.totalIeps;
  const ivaAmount      = (subtotal + iepsAmount) * (ivaRate / 100);
  const total          = subtotal + iepsAmount + ivaAmount;
  const totalBottles   = activeLines.reduce((sum, li) => sum + li.qty, 0);

  return {
    subtotalBeforeDiscount, discountAmount, subtotal,
    iepsAmount, iepsBreakdown: iepsBreakdown.byRate,
    ivaAmount, total, totalBottles,
  };
}
